import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { homeFor, useAuth } from '../auth.jsx';
import HeaderMenu, { Avatar } from './HeaderMenu.jsx';
import JustAttendAd from './JustAttendAd.jsx';
import { helpTopicFor } from '../pages/help/articles/index.js';

/** The account menu: who you are, which company you're working on, profile, sign out. */
export function AccountMenu({ extra = [], profile = true }) {
  const auth = useAuth();
  const { user, organisation, organisations, logout, selectOrganisation } = auth;
  const navigate = useNavigate();
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ');

  const signOut = async () => { await logout(); navigate('/login'); };
  const switchTo = (o) => {
    if (o.id === organisation?.id) return;
    selectOrganisation(o.id);
    navigate(homeFor({ ...auth, organisation: o }));
  };

  return (
    <HeaderMenu className="hmenu-account" align="right" label={`Account menu for ${name || user.email}`}
      trigger={<><Avatar user={user} /><span className="hmenu-account-text"><strong>{name || user.email}</strong>{organisation && <span>{organisation.name}</span>}</span></>}
      items={[
        { heading: <><strong>{name || 'Your account'}</strong><span className="muted">{user.email}</span></> },
        { divider: true },
        // In more than one company (e.g. after accepting an invite): choose which to work on
        ...(organisations.length > 1 ? [
          { heading: 'Working on' },
          ...organisations.map((o) => ({ label: `${o.id === organisation?.id ? '✓ ' : ''}${o.name}`, current: o.id === organisation?.id, onClick: () => switchTo(o) })),
          { divider: true },
        ] : []),
        ...extra,
        ...(profile ? [{ to: '/account', label: 'Edit profile' }, { divider: true }] : []),
        { label: 'Sign out', onClick: signOut },
      ]} />
  );
}

/** Every portal page ends with this: who provides SummitShield, help, contact, privacy. */
export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <JustAttendAd variant="strip" />
        <nav aria-label="Footer" className="footer-links">
          <Link to="/help">Help centre</Link>
          <Link to="/contact">Contact us</Link>
          <Link to="/privacy">Privacy policy</Link>
          <Link to="/terms">Terms</Link>
          <span className="spacer" />
          <span>SummitShield is a product of Just Attend (Veronix Limited).</span>
        </nav>
      </div>
    </footer>
  );
}

export default function Layout() {
  const { organisation, isAdmin } = useAuth();
  const { pathname } = useLocation();
  const manager = ['owner', 'admin'].includes(organisation?.role);
  const inCompany = pathname.startsWith('/company');

  return (
    <div className="shell">
      <header className="topbar">
        <div className="topbar-inner">
          <NavLink to="/dashboard" className="brand"><img src="/logo.png" alt="SummitShield" width="125" height="30" /></NavLink>
          <nav aria-label="Main" className="topnav">
            <NavLink to="/dashboard">Home</NavLink>
            <NavLink to="/inbox">Messages</NavLink>
            <NavLink to="/company/lookalikes">Fake websites</NavLink>
            <NavLink to="/plan">Protection plan</NavLink>
            <NavLink to="/events">Your events</NavLink>
            <HeaderMenu trigger="More" active={inCompany && !pathname.startsWith('/company/lookalikes')} items={[
              { heading: 'Your company' },
              { to: '/company', label: 'Company and domains', end: true },
              { to: '/company/team', label: 'Team' },
              manager && { to: '/company/people', label: 'People' },
              { to: '/company/suppliers', label: 'Suppliers' },
              manager && { to: '/setup', label: 'Setup guide' },
              { divider: true },
              { heading: 'Email and website' },
              { to: '/company/email-security', label: 'Who sends email as you' },
              { to: '/company/email-services', label: 'Services that send your email' },
              { to: '/outbox', label: 'Emails you copied to us' },
              { to: '/company/website', label: 'Your website code' },
              { divider: true },
              { heading: 'Apps' },
              { to: '/stamp/', label: 'Quick Stamp desktop app' },
            ]} />
            {isAdmin && <NavLink to="/admin">Admin</NavLink>}
          </nav>
          <div className="spacer" />
          <Link className="topbar-help" to={pathname.startsWith('/help') ? '/help' : `/help${helpTopicFor(pathname) ? `/${helpTopicFor(pathname)}` : ''}`}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.6.3-1 .9-1 1.7" /><path d="M12 17h.01" /></svg>
            <span className="topbar-help-text">Get help</span>
          </Link>
          <AccountMenu />
        </div>
      </header>
      {organisation?.test_mode && (
        <div className="mode-banner" role="status">
          <div className="mode-banner-inner">
            <strong>Test mode.</strong> Your verify pages aren&rsquo;t public yet, and your check addresses only accept mail from your own domain.
            <Link to="/company">Verify a domain</Link> to go public.
          </div>
        </div>
      )}
      <main className="main">
        <Outlet />
      </main>
      <SiteFooter />
    </div>
  );
}
